import {BackendError} from "./common";
import {GeocodingResponse} from "@mapbox/search-js-core/dist/geocode/GeocodingCore";

export interface GetAddressWithCoordinatesRequest {
    latitude: number,
    longitude: number,
    language: string
}

export interface AcrissCodeProps {
    firstCharacter: string,
    lastCharacter: string,
}

export enum VEHICLE_TYPE {
    car = "CAR",
    bus = "BUS"
}

export enum FUEL_TYPE {
    petrol = "PETROL",
    diesel = "DIESEL",
    electric = 'ELECTRIC',
    unknown = 'UNKNOWN',
    default = "DEFAULT"
}

export enum CAR_CATEGORY {
    small_car = "SMALL_CAR",
    middle_class = "MIDDLE_CLASS",
    upper_class = "UPPER_CLASS",
    suv_jeep = "SUV_JEEP",
    midibus_car = "MIDIBUS_CAR",
    minibus_car = "MINIBUS_CAR"
}

export enum BUS_CATEGORY {
    minibus = "MINIBUS",
    midibus = "MIDIBUS",
    city_bus = "CITY_BUS",
    regional_bus = "REGIONAL_BUS",
    long_distance_coach = "LONG_DISTANCE_COACH",
    luxury_coach = "LUXURY_COACH"
}

export interface CarInfoProps {
    acrissCode: AcrissCodeProps | null,
    carCategory: CAR_CATEGORY | null,
}

export interface CarBusCalculationRequest {
    index: number,
    vehicleType: VEHICLE_TYPE,
    totalPassengerCount: number | null,
    numberOfPassengers: number,
    fuelConsumption: number | null,
    isHybrid: boolean,
    distance: number | null,
    daysOfUse: number | null,
    fuelType: FUEL_TYPE | null,
    carInfo: CarInfoProps | null,
    busCategory: BUS_CATEGORY | null,
}

export interface CarPropsViaAcrissCodeResponse {
    carCategory: CAR_CATEGORY,
    fuelType: FUEL_TYPE,
    errors: BackendError | null
}

export interface GetRouteFromCoordinatesRequest {
    latitude: number,
    longitude: number,
}

export interface GetComputedTotalPassengerCountRequest {
    busCategory: BUS_CATEGORY
}

export interface GetRouteFromCoordinatesResponse {
    code: string
    uuid: string
    waypoints: {
        distance: number
        name: string
        location: [number, number]
    }[]
    routes: {
        distance: number
        duration: number
        geometry: {
            coordinates: [number, number][]
            type: "LineString"
        }
        legs: {
            via_waypoints: [],
            admins: {
                iso_3166_1: string
                iso_3166_1_alpha3: string
            }[]
            distance: number
            duration: number
            steps: []
            summary: string
            weight: number
        }[]
        weight: number
        weight_name: string
    }[]
}

export interface ValidateAcrissCodeResponse {
    isValid: boolean,
    errors: BackendError | null
}

export interface AcrissCodeRequest {
    firstCharacter: string
    lastCharacter: string
}

export interface GetAdressWithCoordinatesResponse extends Omit<GeocodingResponse, "attribution" | "url"> {
    message: string | null,
    errorCode: string | null
}

export interface CarCalculatorPropsResponse {
    vehicleTypes: {
        car: VEHICLE_TYPE.car,
        bus: VEHICLE_TYPE.bus
    },
    fuelTypes: {
        default: FUEL_TYPE.default,
        petrol: FUEL_TYPE.petrol,
        diesel: FUEL_TYPE.diesel,
        electric: FUEL_TYPE.electric
    },
    klimaLinkCarCategories: {
        smallCar: CAR_CATEGORY.small_car,
        middleClass: CAR_CATEGORY.middle_class,
        upperClass: CAR_CATEGORY.upper_class,
        suvJeep: CAR_CATEGORY.suv_jeep,
        minibusCar: CAR_CATEGORY.minibus_car,
        midibusCar: CAR_CATEGORY.midibus_car
    },
    "klimaLinkBusCategories": {
        minibus: BUS_CATEGORY.minibus,
        midibus: BUS_CATEGORY.midibus,
        cityBus: BUS_CATEGORY.city_bus,
        regionalBus: BUS_CATEGORY.regional_bus,
        longDistanceCoach: BUS_CATEGORY.long_distance_coach,
        luxuryCoach: BUS_CATEGORY.luxury_coach
    }
}

export interface GetComputedTotalPassengerCountResponse {
    computedTotalPassengerCount: number  | null,
    errors: BackendError | null
}